/**
 * ATT-3383 admin half — tenant-discovery loading/retry gate.
 *
 * Tenant resolution is registry-only: branding + routing for the current host
 * are fetched at boot by `useTenantDiscovery()`. When that discovery FAILS
 * (timeout / network / 5xx / malformed response / host-not-in-registry), the
 * app must NOT silently render with empty branding — it presents an explicit
 * loading/retry state with a retry affordance until discovery resolves
 * (design.md — graceful-failure decision).
 *
 * ATT-3383 hotfix: while discovery is still IN FLIGHT (`source` is "bootstrap")
 * the gate BLOCKS render with a loading spinner — children fire authenticated
 * requests (e.g. whoami) whose api_url comes from the discovered routing, so
 * they must not run until that routing is in place; otherwise they hit the
 * cross-site canonical base and the host-parent-scoped session cookie isn't
 * sent ("user not found"). Children render once "discovered"; the "failed"
 * state swaps in the retry UI.
 *
 * Localhost/dev is exempt: the local backend may not implement the consolidated
 * `tenantSettings` discovery query, and an empty-branding dev render is fine —
 * blocking it would break local development. Discovery still fires; only the
 * blocking retry screen is skipped there.
 */

import { Button, Result, Spin } from "antd";
import { isLocalhost } from "lib/constants";
import type { ReactNode } from "react";
import LayoutBase from "./Layouts/LayoutBase";

type Props = {
  source: "bootstrap" | "discovered" | "failed";
  /**
   * ATT-3785 — discovery is still in flight but has outrun the soft threshold
   * (almost always a backend cold start). The gate keeps blocking; this only
   * turns a silent spinner into an explained wait.
   */
  slow?: boolean;
  onRetry: () => void;
  /**
   * ATT-3464 — the central auth host is deliberately NOT a tenant, so nothing
   * here may block on resolving one. See the exemption below.
   */
  isCentralAuthHost?: boolean;
  children: ReactNode;
};

export default function TenantDiscoveryGate({
  source,
  slow = false,
  onRetry,
  isCentralAuthHost = false,
  children,
}: Props) {
  // Dev/localhost is exempt: the local backend may not implement the
  // consolidated discovery query and an empty-branding dev render is fine —
  // blocking would break local development.
  if (isLocalhost()) {
    return <>{children}</>;
  }

  // ATT-3464: the central auth host is exempt for the same STRUCTURAL reason as
  // localhost — it is not a tenant and never will be, so "wait for tenant
  // resolution" can only ever end in the retry screen. (That is exactly what
  // shipped and had to be rolled back: UNKNOWN_DOMAIN → permanent retry.)
  //
  // The blocking rationale below does not apply here either. The gate blocks so
  // children cannot fire authenticated requests against the bootstrap `api_url`
  // before the registry routing lands. The only page served on the central host
  // is `/auth/start`, whose sole action is a SAME-ORIGIN `signIn()` — there is
  // no tenant-routed request to protect. The origin tenant's branding is
  // fetched separately (`useTenantDiscovery`'s `brandingHostOverride`), and if
  // that fails the page renders unbranded: a logo that did not load must never
  // stand between a user and a login.
  //
  // False whenever `NEXT_PUBLIC_CENTRAL_AUTH_HOST` is unset, so this branch is
  // unreachable until the cutover flips that variable.
  if (isCentralAuthHost) {
    return <>{children}</>;
  }

  // Discovery resolved → render the app with the registry routing in place.
  if (source === "discovered") {
    return <>{children}</>;
  }

  // ATT-3383 hotfix (cross-site session-cookie regression): while discovery is
  // still IN FLIGHT ("bootstrap"), BLOCK render. Children fire authenticated
  // requests (e.g. whoami) whose api_url comes from the discovered routing; if
  // they run during bootstrap they hit the canonical NEXT_PUBLIC_API_BASE,
  // which is cross-site for legacy/custom domains → the host-parent-scoped
  // session cookie isn't sent → spurious "user not found". Waiting for
  // "discovered" guarantees the registry api_url (same-parent) is in place
  // before any request fires. (The race this closes: first load failed, then a
  // later reload "magically" worked once discovery had warmed and won the race.)
  if (source === "bootstrap") {
    return (
      <LayoutBase title="Loading">
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            gap: 16,
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f5f5f5",
          }}
        >
          <Spin size="large" />
          {/*
            ATT-3785: a cold backend can keep us here for ten-plus seconds. Say
            so rather than letting a bare spinner read as a hang — the previous
            behavior was to give up at 5s and show the retry screen, which is
            what made a healthy-but-cold first load look broken.
          */}
          {slow ? (
            <span data-testid="discovery-slow-hint" style={{ color: "#595959" }}>
              Still getting things ready — this can take a moment on the first
              visit.
            </span>
          ) : null}
        </div>
      </LayoutBase>
    );
  }

  // source === "failed"
  return (
    <LayoutBase title="Loading">
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
        }}
      >
        <Result
          status="warning"
          title="Still securing your connection"
          subTitle="We couldn’t load this site’s settings just now. Check your connection and try again."
          extra={
            <Button type="primary" onClick={onRetry}>
              Try again
            </Button>
          }
        />
      </div>
    </LayoutBase>
  );
}
